import { secureApi } from './secureApi';

/**
 * Pricing guidance API — ops-only, one unit at a time.
 *
 * Every call here is read-only except `savePolicy`, and even that writes only the
 * unit's commercial reference, advisory floor and long-stay tiers. (`refreshAirbnbOffers`
 * makes the backend re-read Channex and update its own saved copy — nothing on Channex.) Nothing in this file can change
 * a published rate: ops does that in the property calendar, which syncs to Channex
 * from there. Keeping that seam is what makes a future automation phase a swap
 * rather than a rewrite (tasks/pricing-guidance-phase1.md decisions 1, 2).
 *
 * Uses the shared `secureApi` instance so the JWT interceptor and the 401 handling
 * are the app's, not this file's. The previous version built its own axios instance
 * with a hand-rolled `x-auth-token` header — a third auth convention for the same
 * app (CLAUDE.md, "Frontend API Layer").
 *
 * ⚠️ `preserveBackendError: true` on every call that renders an error. `secureApi`'s
 * interceptor REPLACES `error.response.data` wholesale on 403/404/500, which destroys
 * the `code` these screens branch on — `RATE_GUIDANCE_UNIT_NOT_FOUND` arrives on a 404
 * and would silently become a generic "unexpected error". Same reason, same flag, as
 * `notificationApi.js:16` and `conversationApi.js`.
 */
const KEEP_CODES = { preserveBackendError: true };

const pricingApi = {
  /**
   * Scoreboard + weekday view + the next 30 days' PUBLISHED prices.
   * Deliberately carries no suggested price — see the service header on the backend.
   */
  getGuidance: async (propertyId) => {
    const response = await secureApi.get(`/api/v1/pricing/unit/${propertyId}/guidance`, KEEP_CODES);
    return response.data.data;
  },

  /**
   * Price an exact stay. `checkOut` is the departure day and is exclusive.
   * The response can legitimately be incomplete — when any night has no published
   * price, every derived figure comes back null and the caller must say the stay
   * cannot be priced rather than render a blank total.
   *
   * `discountPercent` is optional and applies to THIS calculation only — nothing is
   * saved. Sent only when it has a value: the server treats absent as "use the unit's
   * tiers", so posting `discountPercent: ''` on an untouched field would read as an
   * explicit instruction rather than a default.
   */
  getQuote: async (propertyId, { checkIn, checkOut, discountPercent }) => {
    const body = { checkIn, checkOut };
    if (discountPercent !== undefined && discountPercent !== null && discountPercent !== '') {
      body.discountPercent = Number(discountPercent);
    }

    const response = await secureApi.post(
      `/api/v1/pricing/unit/${propertyId}/quote`,
      body,
      KEEP_CODES
    );
    return response.data.data;
  },

  /**
   * Save the commercial reference, the advisory floor and the long-stay tiers.
   * Partial by design — an omitted field is left as it is, not cleared.
   */
  savePolicy: async (propertyId, policy) => {
    const response = await secureApi.put(`/api/v1/pricing/unit/${propertyId}/policy`, policy, KEEP_CODES);
    return response.data.data;
  },

  /**
   * «الخصومات والعروض الحالية» — the unit's Airbnb discounts, running promotions and account rule,
   * from the backend's last saved Channex read. Read-only. A 503 with RATEGUIDE_005 means the
   * feature is switched off and the card should not render.
   */
  getAirbnbOffers: async (propertyId) => {
    const response = await secureApi.get(`/api/v1/pricing/unit/${propertyId}/airbnb-offers`, KEEP_CODES);
    return response.data.data;
  },

  /**
   * «تحديث» — the backend reads the unit's Airbnb account from Channex now (read-only on Channex)
   * and answers `{ refresh: { done, reason? }, offers }`. Always answers with the saved copy, even
   * when Channex fails.
   */
  refreshAirbnbOffers: async (propertyId) => {
    // The backend may wait ~12 s for Channex before answering; the shared 10 s limit would cut it off
    // and report a failure while the read is still going (same override as conversationApi's sends).
    const response = await secureApi.post(
      `/api/v1/pricing/unit/${propertyId}/airbnb-offers/refresh`,
      {},
      { ...KEEP_CODES, timeout: 20000 }
    );
    return response.data.data;
  }
};

export default pricingApi;
